import { useEncounter } from '@/contexts/encounter-context';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { useMediaQuery } from '@/hooks/use-media-query';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { PharmacyType } from '@/api/api.schemas';
import { useUpdateEncountersPreferencesPharmacyType } from '@/api/update-preferred-pharmacy-type';
import { Navigate } from '@/util/react-router-dom';
import { FormButtonRow } from '@/components/containers/form-button-row';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/form';
import { RadioGroup } from '@/components/inputs/radio-group';
import {
  MailOrderPharmacySvg,
  RetailPharmacySvg,
} from '@/components/encounter/pharmacy/pharmacy-svg';
import styles from './pharmacy-selection-page.module.css';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { PHARMACY_TYPES } from '@/util/constants';
import { useAsyncAction } from '@/hooks/use-async-action';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { useSaveProgressProps } from '@/hooks/use-save-progress-props';
import { requiredEnum } from '@/util/validators';
import { useTranslation } from '@/contexts/i18n-context';

const translationBase = 'visit.pharmacySelection';

const PharmacyTypeSelectionForm = z.object({
  pharmacyType: requiredEnum(PharmacyType, `${translationBase}.error.notSelectedType`),
});

type FormValues = z.infer<typeof PharmacyTypeSelectionForm>;

export function PharmacyTypeSelectionPage() {
  const { t } = useTranslation();
  const { encounter, invalidateEncounter } = useEncounter();
  const { navigatePostSubmit } = usePostSubmitNavigation();

  const { mutateAsync: setPreferredPharmacyType } = useUpdateEncountersPreferencesPharmacyType();
  const isMobile = useMediaQuery('--screen-md');

  const form = useForm<FormValues>({
    resolver: zodResolver(PharmacyTypeSelectionForm),
    mode: 'onBlur',
  });

  const { trigger: setPreferredPharmacyTypeAsync, loading: isSettingPreferredPharmacyType } =
    useAsyncAction(async (data: FormValues, isIncomplete: boolean) => {
      await setPreferredPharmacyType({
        encounterId: encounter.id,
        data,
      });

      if (!isIncomplete) {
        // If the form is incomplete we don't want to invalidate the encounter
        // because this will cause the user to be automatically redirected to the choose pharmacy page
        await invalidateEncounter();
      }

      navigatePostSubmit({
        isIncomplete,
        policy: {
          resolveComplete: () => ({
            path: `/visit/${encounter.id}/choose-pharmacy`,
            replace: true,
          }),
        },
      });
    });

  const onSubmit = (data: FormValues, isIncomplete: boolean) => {
    setPreferredPharmacyTypeAsync(data, isIncomplete);
  };

  const saveProgressProps = useSaveProgressProps(form, (data) => onSubmit(data, true));

  if (encounter.preferredPharmacyType) {
    return <Navigate to={`/visit/${encounter.id}/choose-pharmacy`} replace={true} />;
  }

  return (
    <PageContainer>
      {!isMobile && <VisitTitleButtons saveProgress={saveProgressProps} />}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons />}
        <VisitNavigation />
        <h1 className="h3">{t(`${translationBase}.formTitle.title`)}</h1>
        <p>{t(`${translationBase}.formTitle.description`)}</p>
        {isMobile && <SaveProgressButton {...saveProgressProps} />}
      </GradientFormTitle>
      <Form form={form} onSubmit={(data) => onSubmit(data, false)}>
        <Section className={styles.container}>
          <h1 className="h4">{t(`${translationBase}.paragraph`)}</h1>
          <RadioGroup
            label={t(`${translationBase}.pharmacyTypeLabel`)}
            readersOnlyLabel
            variant="selectorCard"
            name="pharmacyType"
            options={PHARMACY_TYPES.map((type) => ({
              value: type,
              label: t(`${translationBase}.buttons.${type}`),
              icon:
                type === PharmacyType.Retail ? (
                  <RetailPharmacySvg className={styles.svg} />
                ) : (
                  <MailOrderPharmacySvg className={styles.svg} />
                ),
            }))}
          />
        </Section>
        <FormButtonRow
          type="submit"
          buttonTextKey={`${translationBase}.buttons.submit`}
          loading={isSettingPreferredPharmacyType}
        />
      </Form>
    </PageContainer>
  );
}
